import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
// SERVICES
import DiscountCouponsService from "../../api/discountCoupons";
// APP
import RemoveModal from "../modals/removeModal";
import CreateOrEditDiscountCouponModal from "./createOrEditDiscountCouponModal";
// MATERIAL UI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import { alpha, useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";

// ICONS
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DiscountIcon from "@mui/icons-material/Discount";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VerifiedIcon from "@mui/icons-material/Verified";
import NewReleasesIcon from "@mui/icons-material/NewReleases";

import { dispatchSnackBarMessage } from "../../utils/snackbar";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -10,
    transition: {
      duration: 0.2,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

// Custom component for the discount value chip
const DiscountValueChip = ({ value }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: 70,
          height: 70,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontWeight: "bold",
          fontSize: "1.5rem",
          transform: "rotate(-15deg)",
          boxShadow: theme.shadows[5],
          border: "2px solid white",
          zIndex: 1,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          {value}
          <Typography
            component="span"
            sx={{
              fontSize: "1rem",
              verticalAlign: "top",
              marginLeft: "-2px",
            }}
          >
            %
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
};

// Coupon card component
const CouponCard = ({ coupon, onEdit, onDelete }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(coupon.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Check if coupon is new (created less than 7 days ago)
  const isNew = () => {
    if (!coupon.created_at) return false;
    const createdDate = new Date(coupon.created_at);
    const now = new Date();
    const diffTime = Math.abs(now - createdDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        position: "relative",
        overflow: "visible",
        boxShadow: theme.shadows[2],
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[6],
          transform: "translateY(-3px)",
        },
        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        height: "100%",
      }}
    >
      {/* New badge if needed */}
      {isNew() && (
        <Box
          sx={{
            position: "absolute",
            top: -10,
            left: -10,
            zIndex: 2,
          }}
        >
          <Tooltip title="Novo cupom">
            <NewReleasesIcon
              color="secondary"
              sx={{
                fontSize: 30,
                filter: "drop-shadow(0px 2px 2px rgba(0,0,0,0.2))",
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Discount value badge */}
      <Box
        sx={{
          position: "absolute",
          top: -20,
          right: 15,
          zIndex: 2,
        }}
      >
        <DiscountValueChip value={coupon.value} />
      </Box>

      <CardContent sx={{ pt: 4, pb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <LocalOfferIcon
                sx={{
                  color: theme.palette.primary.main,
                  mr: 1.5,
                  fontSize: 28,
                }}
              />
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  {coupon.code}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 0.5 }}
                >
                  {coupon.description || "Cupom de desconto"}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Copiar código">
                  <Button
                    startIcon={copied ? <VerifiedIcon /> : <ContentCopyIcon />}
                    size="small"
                    color={copied ? "success" : "primary"}
                    variant="outlined"
                    onClick={handleCopyCode}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      minWidth: 110,
                    }}
                  >
                    {copied ? "Copiado!" : "Copiar"}
                  </Button>
                </Tooltip>
              </Box>

              <Stack direction="row" spacing={1}>
                <Tooltip title="Editar cupom">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      size="small"
                      aria-label="edit"
                      color="primary"
                      onClick={() => onEdit(coupon)}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.2
                          ),
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </motion.div>
                </Tooltip>
                <Tooltip title="Excluir cupom">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconButton
                      size="small"
                      aria-label="delete"
                      color="error"
                      onClick={() => onDelete(coupon)}
                      sx={{
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.error.main, 0.2),
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </motion.div>
                </Tooltip>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const DiscountCoupons = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [discountCoupons, setDiscountCoupons] = useState([]);
  const [editingDiscountCoupon, setEditingDiscountCoupon] = useState(null);
  const [showDiscountCouponModal, setDiscountCouponModal] = useState(false);

  const [discountCouponsToDelete, setDiscountCouponToDelete] = useState(null);
  const [showDiscountCouponDeleteModal, setShowDiscountCouponDeleteModal] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setTimeout(async () => {
      await loadDiscountCoupons();
    }, 10);
  }, []);

  const loadDiscountCoupons = async () => {
    try {
      setLoading(true);
      const res = await DiscountCouponsService.getDiscountCoupons();
      setDiscountCoupons(res.data);
    } catch (e) {
      setErrorMessage(
        "Erro ao carregar seus cupons de desconto, se o erro persistir contate o suporte"
      );
      setShowError(true);
      handleError(
        "Erro ao carregar seus cupons de desconto, se o erro persistir contate o suporte"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountCouponModal = () => {
    setDiscountCouponModal(!showDiscountCouponModal);
    setEditingDiscountCoupon(null);
  };

  const createDiscountCoupon = async (paymentMethodObj) => {
    try {
      await DiscountCouponsService.createDiscountCoupon(paymentMethodObj);
      handleDiscountCouponModal();
      await loadDiscountCoupons();
      dispatchSnackBarMessage(
        `create-discount-coupon-success-${new Date()}`,
        "Cupom de desconto criado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }
      handleError(messages || "Erro ao criar cupom de desconto");
    }
  };

  const handleEditDiscountCoupon = (discountCoupon) => {
    setEditingDiscountCoupon(discountCoupon);
    setDiscountCouponModal(!showDiscountCouponModal);
  };

  const editDiscountCoupon = async (discountCoupon) => {
    try {
      await DiscountCouponsService.editDiscountCoupon(
        discountCoupon,
        discountCoupon.id
      );

      handleDiscountCouponModal();
      setEditingDiscountCoupon(null);
      await loadDiscountCoupons();
      dispatchSnackBarMessage(
        `edit-discount-coupon-success-${new Date()}`,
        "Cupom de desconto atualizado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      if (typeof errorData === "object") {
        for (const prop in errorData) {
          if (errorData.hasOwnProperty(prop)) {
            messages += errorData[prop].join("\n") + "\n";
          }
        }
      } else {
        messages = "Erro ao editar cupom de desconto";
      }
      handleError(messages);
    }
  };

  const handleDeleteDiscountCouponModal = (discountCoupon) => {
    setDiscountCouponToDelete(discountCoupon);
    setShowDiscountCouponDeleteModal(!showDiscountCouponDeleteModal);
  };

  const handleDeleteDiscountCoupon = async () => {
    try {
      await DiscountCouponsService.deleteDiscountCoupon(
        discountCouponsToDelete.id
      );
      setDiscountCouponToDelete(null);
      setShowDiscountCouponDeleteModal(false);
      await loadDiscountCoupons();
      dispatchSnackBarMessage(
        `delete-discount-coupon-success-${new Date()}`,
        "Cupom de desconto removido com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data;
      handleError(
        typeof errorData === "string"
          ? errorData
          : "Erro ao excluir cupom de desconto"
      );
      setShowDiscountCouponDeleteModal(false);
    }
  };

  const handleError = (message = null) => {
    setErrorMessage(message);
    setShowError(true);
    dispatchSnackBarMessage(`discount-config-${Date.now()}`, message, "error");
  };

  if (loading) {
    return (
      <Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box sx={{ p: 3 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              sx={{ mb: 2, borderRadius: 2 }}
            />
            <Grid container spacing={2}>
              {[1, 2, 3].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={160}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Collapse in={showError} sx={{ mb: 2 }}>
          <Alert
            severity="error"
            onClose={() => setShowError(false)}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        </Collapse>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={handleDiscountCouponModal}
                color="primary"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: "medium",
                  boxShadow: theme.shadows[3],
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                }}
              >
                Novo Cupom de Desconto
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        <Box
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            borderRadius: 3,
            p: 2,
            boxShadow: theme.shadows[1],
            backdropFilter: "blur(8px)",
          }}
        >
          {discountCoupons.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  px: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  borderStyle: "dashed",
                  borderWidth: 1,
                  borderColor: theme.palette.divider,
                }}
              >
                <DiscountIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.action.disabled,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Nenhum cupom de desconto cadastrado
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ mb: 2, maxWidth: 400 }}
                >
                  Crie cupons de desconto para suas promoções e atraia mais
                  clientes.
                </Typography>
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={handleDiscountCouponModal}
                    sx={{ mt: 1, borderRadius: 2 }}
                  >
                    Adicionar Cupom
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Grid container spacing={3}>
                <AnimatePresence>
                  {discountCoupons.map((discountCoupon) => (
                    <Grid item xs={12} sm={6} md={4} key={discountCoupon.id}>
                      <motion.div variants={itemVariants} exit="exit" layout>
                        <CouponCard
                          coupon={discountCoupon}
                          onEdit={handleEditDiscountCoupon}
                          onDelete={handleDeleteDiscountCouponModal}
                        />
                      </motion.div>
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            </motion.div>
          )}
        </Box>
      </Box>

      <RemoveModal
        title="Excluir Cupom de Desconto"
        message={`Tem certeza que deseja excluir o cupom de desconto "${discountCouponsToDelete?.code}" com ${discountCouponsToDelete?.value}% de desconto?`}
        show={showDiscountCouponDeleteModal}
        toggleModal={handleDeleteDiscountCouponModal}
        onRemove={handleDeleteDiscountCoupon}
      />

      {showDiscountCouponModal && (
        <CreateOrEditDiscountCouponModal
          show={showDiscountCouponModal}
          closeModal={handleDiscountCouponModal}
          createDiscountCoupon={createDiscountCoupon}
          editDiscountCoupon={editDiscountCoupon}
          discountCoupon={editingDiscountCoupon}
        />
      )}
    </>
  );
};

export default DiscountCoupons;
